import React, { useEffect, useRef, useState } from 'react';
import { AnyIfEmpty, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import iconAddress from '../../../images/icons/icon-address-point.png';
import ReactSlider from 'react-slider';
import { bathrooms, bedrooms, sortBy } from '../../../constants';
import iconDollar from '../../../images/icons/icon-Dollar.svg';
import iconBed from '../../../images/icons/icon-double-bed.svg';
import iconReset from '../../../images/icons/icon-reset.svg';
import iconSquareFeet from '../../../images/icons/icon-square-feet.svg';
import iconFilter from '../../../images/icons/icon-sort.svg';
import banner from '../../../images/no-unit-image.svg';
import iconBath from '../../../images/icons/icon-bath.svg';
import { unitsProps } from '../../../interface/LocationInterface';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { getUnitsByLocationId } from '../../../redux/unit/actions/unitsByLocationId.action';
import Loader from '../../common/loader/Loader';
import UnitsCard from './UnitsCard/UnitsCard';
import styles from './UnitsDashboard.module.css';
import Popup from '../../common/popup/Popup';
import UnitsFilterPopup from './UnitsFilterPopup';
import { clearPreviousLocation, getLocationById, getLocationFilters, resetLocationFilters } from '../../../redux/location/actions/location.action';
import Slider from '../../common/slider/Slider';
import { getTime } from '../../../redux/unit/actions/unit.action';
import Faq from '../../tourPortal/faq/Faq';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import Lightbox from '../../common/lightbox/Lightbox';
import { SliderWithLightbox } from '../../common/slider-with-lightbox/SliderWithLightBox';
import NoUnits from '../../../images/no-units-found.svg';
import ClockIcon from '../../icons/ClockIcon';
import InfoIcon from '../../icons/InfoIcon';
import { getLocationTimeZone } from '../../../utils/constants/constants';
import ApartmentIcon from '../../icons/ApartmentIcon';

const useOutsideAlerter = (
    ref: React.RefObject<HTMLDivElement | null>,
    setShowDropDown: (arg: any) => void,
    dropdownStatusObj: {
        bath: boolean;
        bed: boolean;
        square: boolean;
        price: boolean;
        sortBy: boolean;
    },
) => {
    useEffect(() => {
        const handleClickOutside = (event: AnyIfEmpty<object>) => {
            if (ref.current && !ref.current.contains(event.target)) {
                // Close all dropdowns when clicking outside
                setShowDropDown({
                    bath: false,
                    bed: false,
                    square: false,
                    price: false,
                    sortBy: false,
                });
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, setShowDropDown]);
};

const UnitsDashboard = () => {
    const [open, setIsOpen] = useState(false);
    const locationId = useParams().id;
    const navigate = useNavigate();

    const unitsByLocationId = useSelector((state: IRootState) => state.unitsByLocationId.data);
    const loading = useSelector((state: IRootState) => state.unitsByLocationId.loading);
    // const locationName = useSelector((state: IRootState) => state.locationReducer.locationById.data?.location?.name);
    const locationLoading = useSelector((state: IRootState) => state.locationReducer.locationById.loading);

    // Location Filters State
    const { data: locationFiltersData, loading: locationFiltersLoading, error: locationFiltersError } = useSelector((state: IRootState) => state.locationReducer.locationFilters);

    // Extract filter data from API response
    const filterData = locationFiltersData?.data || locationFiltersData || null;
    const hasRooms = filterData?.rooms && Array.isArray(filterData.rooms) && filterData.rooms.length > 0;
    const hasBaths = filterData?.baths && Array.isArray(filterData.baths) && filterData.baths.length > 0;
    const hasPrice = filterData?.price && typeof filterData.price === 'object' && filterData.price.min !== undefined && filterData.price.max !== undefined;
    const hasSize = filterData?.size && typeof filterData.size === 'object' && filterData.size.min !== undefined && filterData.size.max !== undefined;
    const hasSortBy = filterData?.sortBy && Array.isArray(filterData.sortBy) && filterData.sortBy.length > 0;

    // Filter bedrooms options based on API response
    const availableBedrooms = hasRooms
        ? bedrooms.filter(bed => bed.value === '' || filterData.rooms.map(Number).includes(Number(bed.value)))
        : [];

    // Create dynamic bathroom options from API response (handles decimals like 2.5)
    const availableBathrooms = hasBaths && filterData?.baths
        ? filterData.baths
            .slice()
            .sort((a: number, b: number) => a - b) // Sort ascending
            .map((bathValue: number) => {
                // Check if it exists in constants first
                const existingBath = bathrooms.find(b => b.value === bathValue);
                if (existingBath) {
                    return existingBath;
                }
                // Create dynamic entry for values not in constants (e.g., 2.5)
                const isInteger = Number.isInteger(bathValue);
                const displayValue = isInteger ? Math.floor(bathValue) : bathValue;
                return {
                    name: `${displayValue} ${bathValue === 1 ? 'Bathroom' : 'Bathrooms'}`,
                    id: `bathroom-${bathValue.toString().replace('.', '-')}`,
                    value: bathValue,
                };
            })
        : [];

    // Filter sortBy options based on API response
    const availableSortBy = hasSortBy
        ? sortBy.filter(sort => filterData.sortBy.includes(sort.name))
        : sortBy;

    const locationdetail = useSelector((state: IRootState) => state.locationReducer.locationById.data?.location);
    const locationObject = useSelector((state: IRootState) => state.locationReducer.locationById.data);

    const dropdownStatusObj = {
        bath: false,
        bed: false,
        square: false,
        price: false,
        sortBy: false,
    };

    const resetObj = {
        minPrice: '',
        maxPrice: '',
        bed: [],
        minSize: '',
        maxSize: '',
        bath: [],
        sortBy: locationdetail?.isPriceListed ? 'price' : 'rooms',
        sortOrder: 'ASC',
    };

    const dispatch: AppDispatch = useDispatch();

    useEffect(() => {
        if (locationObject && Object.keys(locationObject).length && locationObject.status === false && locationObject.statusCode === 404) {
            dispatch(clearPreviousLocation());
            navigate('/appointments');
        }
    }, [locationObject]);

    // Track if filters have been initialized from API
    const hasInitializedFilters = useRef(false);

    // Reset refs when locationId changes to allow fetching for new location
    useEffect(() => {
        if (fetchedFiltersLocationId.current !== locationId) {
            hasFetchedLocationFilters.current = false;
            hasFetchedInitialUnits.current = false;
            initializedFiltersFromLocation.current = false;
            fetchedFiltersLocationId.current = null;
            isInitialMount.current = true;
            prevFiltersRef.current = '';
            hasInitializedFilters.current = false;
        }
    }, [locationId]);

    // Fetch location by ID - only once when locationId is available
    useEffect(() => {
        if (locationId) {
            dispatch(getLocationById(locationId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId]);

    // Fetch location filters once location details are retrieved - only once per locationId
    useEffect(() => {
        if (locationdetail && !locationLoading && locationId &&
            !hasFetchedLocationFilters.current &&
            fetchedFiltersLocationId.current !== locationId) {
            hasFetchedLocationFilters.current = true;
            fetchedFiltersLocationId.current = locationId;
            dispatch(getLocationFilters(locationId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationdetail, locationLoading, locationId]);

    // Initialize filters from API response when data is available - only once
    useEffect(() => {
        if (filterData && !locationFiltersLoading && locationId && !hasInitializedFilters.current) {
            hasInitializedFilters.current = true;
            const newFilters: AnyIfEmpty<object> = {
                minPrice: hasPrice ? filterData.price.min : '',
                maxPrice: hasPrice ? filterData.price.max : '',
                bed: [],
                minSize: hasSize ? filterData.size.min : 0,
                maxSize: hasSize ? filterData.size.max : 10000,
                bath: [],
                sortBy: locationdetail?.isPriceListed ? 'price' : 'rooms',
                sortOrder: 'ASC',
            };

            // Set default sortBy based on available options
            if (hasSortBy && availableSortBy.length > 0) {
                const defaultSort = locationdetail?.isPriceListed
                    ? availableSortBy.find(s => s.sortOrder === 'price') || availableSortBy[0]
                    : availableSortBy.find(s => s.sortOrder === 'rooms') || availableSortBy[0];

                if (defaultSort) {
                    newFilters.sortBy = defaultSort.sortOrder;
                    newFilters.sortOrder = defaultSort.sort;
                }
            }

            // Update prevFiltersRef BEFORE setting filters to prevent duplicate API calls
            const filtersString = JSON.stringify(newFilters);
            prevFiltersRef.current = filtersString;
            initialFiltersJSON.current = filtersString;
            setFilters(newFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterData, locationFiltersLoading, locationId]);

    // Reset filters and refs on component unmount
    useEffect(() => {
        return () => {
            hasFetchedLocationFilters.current = false;
            hasFetchedInitialUnits.current = false;
            initializedFiltersFromLocation.current = false;
            fetchedFiltersLocationId.current = null;
            isInitialMount.current = true;
            prevFiltersRef.current = '';
            dispatch(resetLocationFilters());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [filters, setFilters]: AnyIfEmpty<object> = useState({
        minPrice: '',
        maxPrice: '',
        bed: [],
        minSize: 0,
        maxSize: 10000,
        bath: [],
        sortBy: 'price',
        sortOrder: 'ASC',
    });

    const [rFilters, setRfilters]: AnyIfEmpty<object> = useState({
        minPrice: '',
        maxPrice: '',
        bed: [],
        minSize: '',
        maxSize: '',
        bath: [],
        sortBy: 'price',
        sortOrder: 'ASC',
    });

    const [showDropDown, setShowDropDown]: AnyIfEmpty<object> = useState(dropdownStatusObj);
    const [startIndex, setStartIndex] = useState(0);
    const [showLightBox, setShowLightBox] = useState(false);
    const [hasUnitsAvailable, setHasUnitsAvailable] = useState(false);

    const wrapperRef = useRef<HTMLElement | null>(null);
    useOutsideAlerter(wrapperRef as React.RefObject<HTMLDivElement>, setShowDropDown, dropdownStatusObj);

    // Refs to track if APIs have been called to prevent duplicate calls
    const hasFetchedLocationFilters = useRef(false);
    const hasFetchedInitialUnits = useRef(false);
    const fetchedFiltersLocationId = useRef<string | null>(null);
    const initializedFiltersFromLocation = useRef(false);
    const prevFiltersRef = useRef<string>('');
    const initialFiltersJSON = useRef<string | null>(null);
    const isInitialMount = useRef(true);

    // useEffect(() => {
    //     document.addEventListener('click', handleCarouselClick);
    //     return () => {
    //         document.removeEventListener('click', handleCarouselClick);
    //     };
    // }, []);

    // Fetch units - once on initial load, then whenever filters change
    useEffect(() => {
        if (locationId) {
            const filtersString = JSON.stringify(filters);
            const filtersChanged = prevFiltersRef.current !== filtersString;

            // Only call API on initial mount OR when filters actually change
            // Skip if filters haven't been initialized yet (prevFiltersRef is empty and this is initial mount)
            if ((isInitialMount.current || filtersChanged) && prevFiltersRef.current !== '') {
                if (isInitialMount.current) {
                    isInitialMount.current = false;
                    hasFetchedInitialUnits.current = true;
                }
                // Update prevFiltersRef BEFORE dispatching to prevent duplicate calls
                prevFiltersRef.current = filtersString;
                dispatch(getUnitsByLocationId(locationId, filters));
                setShowDropDown({ ...showDropDown, sortBy: false });
            } else if (isInitialMount.current && prevFiltersRef.current === '') {
                // If initial mount but filters not initialized yet, just update the ref to prevent duplicate
                prevFiltersRef.current = filtersString;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, locationId]);

    const clickForLightboxHandler = (index: number) => {
        setStartIndex(index);
        setShowLightBox(true);
        document.body.classList.add('disable-scrolling');
    };

    useEffect(() => {
        if (locationdetail && !locationdetail?.isPriceListed && !initializedFiltersFromLocation.current) {
            initializedFiltersFromLocation.current = true;
            const locationFilters = {
                minPrice: '',
                maxPrice: '',
                bed: [],
                minSize: 0,
                maxSize: 10000,
                bath: [],
                sortBy: 'rooms',
                sortOrder: 'ASC',
            };
            // Update prevFiltersRef BEFORE setting filters to prevent duplicate API calls
            const filtersString = JSON.stringify(locationFilters);
            prevFiltersRef.current = filtersString;
            initialFiltersJSON.current = filtersString;
            setFilters(locationFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationdetail]);

    // const handleCarouselClick = (event: any) => {
    //     if (event.target instanceof HTMLElement && event.target.closest('.carousel')) {
    //         const clickedIndex = Number(event.target.getAttribute('data-index'));
    //         clickForLightboxHandler(clickedIndex);
    //     }
    // };

    useEffect(() => {
        if (unitsByLocationId?.units && unitsByLocationId.units.length > 0) {
            setHasUnitsAvailable(true);
        }
    }, [unitsByLocationId?.units]);

    const handleBedroomChecked = (value: number | string, key: string) => {
        let prev = key === 'bedrooms' ? filters?.bed : filters?.bath;
        // Use strict comparison - 1 and 1.0 are treated as different values
        const bedroomIndex = prev.indexOf(value);
        if (bedroomIndex !== -1) {
            prev.splice(bedroomIndex, 1);
        } else {
            if (value === '') {
                prev = [];
            } else {
                prev.push(value);
            }
        }
        key === 'bedrooms' ? setFilters({ ...filters, bed: [...prev] }) : setFilters({ ...filters, bath: [...prev] });
    };

    const showDropDownHandler = (dropDownName: string) => {
        const isCurrentlyOpen = showDropDown[dropDownName];
        // Close all dropdowns first, then open the clicked one if it wasn't open
        setShowDropDown({
            bath: false,
            bed: false,
            square: false,
            price: false,
            sortBy: false,
            [dropDownName]: !isCurrentlyOpen, // Toggle the clicked dropdown
        });
    };

    // Sync rFilters with filters when popup opens
    useEffect(() => {
        if (open) {
            // When popup opens, sync rFilters with current filters
            // This ensures the popup shows the current filter state, including sortBy selection
            setRfilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const resetHandler = () => {
        // Reset filters based on API data if available, otherwise use resetObj
        if (filterData) {
            const resetFilters: AnyIfEmpty<object> = {
                minPrice: hasPrice ? filterData.price.min : '',
                maxPrice: hasPrice ? filterData.price.max : '',
                bed: [],
                minSize: hasSize ? filterData.size.min : 0,
                maxSize: hasSize ? filterData.size.max : 10000,
                bath: [],
                sortBy: locationdetail?.isPriceListed ? 'price' : 'rooms',
                sortOrder: 'ASC',
            };

            // Set default sortBy based on available options
            if (hasSortBy && availableSortBy.length > 0) {
                const defaultSort = locationdetail?.isPriceListed
                    ? availableSortBy.find(s => s.sortOrder === 'price') || availableSortBy[0]
                    : availableSortBy.find(s => s.sortOrder === 'rooms') || availableSortBy[0];

                if (defaultSort) {
                    resetFilters.sortBy = defaultSort.sortOrder;
                    resetFilters.sortOrder = defaultSort.sort;
                }
            }

            setFilters(resetFilters);
        } else {
            setFilters(resetObj);
        }
        setShowDropDown(dropdownStatusObj);
    };

    return (
        <>
            {/* <OuterHeader /> */}
            {/* <Lightbox
                setShowLightBox={setShowLightBox}
                images={locationdetail?.images}
                showLightBox={showLightBox}
                startIndex={startIndex}
            /> */}
            {loading || locationLoading || locationFiltersLoading ? (
                <Loader />
            ) : (
                <div className={`${styles.container} location-container`}>
                    <Popup
                        open={open}
                        setOpen={setIsOpen}
                        bodyData={
                            <UnitsFilterPopup
                                filters={rFilters}
                                setFilters={setRfilters}
                                open={open}
                                setOpen={setIsOpen}
                                onApplyFilters={(appliedFilters) => {
                                    // Update main filters state when filters are applied
                                    setFilters(appliedFilters);
                                }}
                            />
                        }
                    />

                    <div className={styles.UnitGreetings}>
                        <div className={styles.titleWrapper}>
                            <h2 className={styles.greetingTitle}>
                                Welcome to <span>{locationdetail?.name} </span>
                            </h2>
                            {hasUnitsAvailable && (
                                <span className={styles.sortWrapper} onClick={() => setIsOpen(true)}>
                                    <img src={iconFilter} alt="Filter" /> Filter
                                </span>
                            )}
                        </div>

                        {/* <p className={styles.greetingDesc}>
                            Ready to schedule your self-guided tour? Please begin by selecting the apartment you'd like to see.
                        </p> */}
                    </div>

                    <div className={`${styles.unitCardOuter}`}>
                        <div className={`${styles.unitCard} unitCard`}>
                            <div className="locationCard">
                                {/* {locationdetail?.images ? ( */}
                                <SliderWithLightbox
                                    imgs={locationdetail?.images}
                                    setShowLightBox={setShowLightBox}
                                    showLightBox={showLightBox}
                                    startIndex={startIndex}
                                    onClickHandler={clickForLightboxHandler}
                                />

                                {/* <Slider images={locationdetail?.images} /> */}
                                {/* <Carousel
                                    showArrows={true}
                                    autoPlay={true}
                                    infiniteLoop={true}
                                    emulateTouch
                                    preventMovementUntilSwipeScrollTolerance={true}
                                    swipeScrollTolerance={50}>
                                    {locationdetail && locationdetail?.images && !!locationdetail?.images.length ? (
                                        locationdetail?.images.map((img: string, index: number) => (
                                            <div key={index}>
                                                <img src={img} alt="location-image" />
                                            </div>
                                        ))
                                    ) : (
                                        <div>
                                            <img src={banner} alt="" />
                                        </div>
                                    )}
                                </Carousel> */}
                            </div>
                            <div className={styles.locationInfoWrapper}>
                                <div className={styles.locationInfo}>
                                    <p className="address-heading">
                                        <span className={styles.iconAddressWrapper}>
                                            <img src={iconAddress} alt="" />
                                        </span>
                                        <span>{`${locationdetail?.street1 ? locationdetail?.street1 : ''}
                                          ${locationdetail?.street2 ? `,${locationdetail?.street2}` : ''},
                                           ${locationdetail?.city ? locationdetail?.city : ''},
                                            ${locationdetail?.state ? locationdetail?.state : ''}
                                             ${locationdetail?.country ? locationdetail?.country : ''}`}</span>
                                        {/* <span className={styles.timeZone}>{locationdetail?.timeZone}</span> */}
                                    </p>
                                    {/* <h6>Description</h6> */}
                                    <p className="address-text">{locationdetail?.description}</p>
                                    <div className={styles.timeZoneWrapper}>
                                        <ClockIcon />
                                        <span>{getLocationTimeZone(locationdetail?.timeZone)}</span>
                                    </div>

                                    <div className={styles.disclaimer}>
                                        <InfoIcon />
                                        <p>{`The appointment date and time are based on the ${getLocationTimeZone(
                                            locationdetail?.timeZone,
                                        )} timezone. All scheduled appointments will adhere to this timezone. If you have any questions or concerns, please contact our support team.`}</p>
                                    </div>

                                    {locationObject && locationObject?.isAmenitiesExist && <div className={styles.amenitiesWrapper}>
                                        {/* <p><strong>{locationdetail?.name}</strong> offers a variety of amenities to enhance your living experience. View the amenities available at <strong>{locationdetail?.name}</strong> below.</p> */}
                                        <button className={styles.amenitiesButton} onClick={() => navigate(`/location/${locationId}/amenities`)}
                                            title="View Amenities" aria-label="View Amenities">
                                            <ApartmentIcon size={24} color="#41896b" />
                                            <span> View Amenities</span>
                                        </button>
                                    </div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {hasUnitsAvailable && (
                        <div ref={wrapperRef as React.RefObject<HTMLDivElement>} className={styles.filters}>
                            {/* Bedrooms Filter - Only show if API has rooms data */}
                            {hasRooms && availableBedrooms.length > 0 && (
                                <div className={styles.filter}>
                                    <button className={showDropDown.bed ? `${styles.active}` : ''} onClick={() => showDropDownHandler('bed')}>
                                        <img className={styles.iconFilter} src={iconBed} alt="" />
                                        Bedrooms
                                    </button>
                                    <div
                                        className={
                                            showDropDown.bed
                                                ? `${styles.filterContainer} ${styles.show}`
                                                : `${styles.filterContainer} ${styles.hide}`
                                        }>
                                        {availableBedrooms.map(({ name, id, value }, index) => (
                                            <div key={index} className={styles.checkboxContainer}>
                                                <input
                                                    type="checkbox"
                                                    id={id}
                                                    checked={filters.bed.length > 0 ? filters.bed?.includes(value) : value === ''}
                                                    onChange={() => handleBedroomChecked(value, 'bedrooms')}
                                                />
                                                <label htmlFor={id}>{name}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Bathrooms Filter - Only show if API has baths data */}
                            {hasBaths && availableBathrooms.length > 0 && (
                                <div className={styles.filter}>
                                    <button className={showDropDown.bath ? `${styles.active}` : ''} onClick={() => showDropDownHandler('bath')}>
                                        <img className={styles.iconFilter} src={iconBath} alt="" />
                                        Bathrooms
                                    </button>
                                    <div
                                        className={
                                            showDropDown.bath
                                                ? `${styles.filterContainer} ${styles.show}`
                                                : `${styles.filterContainer} ${styles.hide}`
                                        }>
                                        {availableBathrooms.map((item: { name: string; id: string; value: number | string }, index: number) => (
                                            <div key={index} className={styles.checkboxContainer}>
                                                <input
                                                    type="checkbox"
                                                    id={item.id}
                                                    checked={filters?.bath?.includes(item.value)}
                                                    onChange={() => handleBedroomChecked(item.value, 'bathrooms')}
                                                />
                                                <label htmlFor={item.id}>{item.name}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Square Footage Filter - Only show if API has size data */}
                            {hasSize && (
                                <div className={styles.filter}>
                                    <button onClick={() => showDropDownHandler('square')} className={showDropDown.square ? `${styles.active}` : ''}>
                                        <img className={styles.iconFilter} src={iconSquareFeet} alt="" />
                                        Square footage
                                    </button>
                                    <div
                                        className={
                                            showDropDown.square
                                                ? `${styles.filterContainer} ${styles.show}`
                                                : `${styles.filterContainer} ${styles.hide}`
                                        }>
                                        <div className={styles.rangeSliderWrapper}>
                                            <ReactSlider
                                                className="horizontal-slider"
                                                thumbClassName="thumb"
                                                trackClassName="track"
                                                min={filterData.size.min}
                                                max={filterData.size.max}
                                                onAfterChange={(e) => setFilters({ ...filters, minSize: e[0], maxSize: e[1] })}
                                                value={[Number(filters?.minSize) || filterData.size.min, Number(filters?.maxSize) || filterData.size.max]}
                                                ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
                                                renderThumb={(props, state) => {
                                                    const { key, ...restProps } = props;
                                                    return <div key={key} {...restProps}>{`${state.valueNow}`}</div>;
                                                }}
                                                pearling
                                                minDistance={10}
                                            />
                                            <p></p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Price Filter - Only show if API has price data */}
                            {hasPrice && (
                                <div className={styles.filter}>
                                    <button
                                        onClick={() => showDropDownHandler('price')}
                                        className={showDropDown.price ? `${styles.active}` : ''}>
                                        <img className={styles.iconFilter} src={iconDollar} alt="" />
                                        Select Price
                                    </button>
                                    <div
                                        className={
                                            showDropDown.price
                                                ? `${styles.filterContainer} ${styles.show}`
                                                : `${styles.filterContainer} ${styles.hide}`
                                        }>
                                        <div className={styles.rangeSliderWrapper}>
                                            <ReactSlider
                                                className="horizontal-slider"
                                                thumbClassName="thumb"
                                                trackClassName="track"
                                                min={filterData.price.min}
                                                max={filterData.price.max}
                                                onAfterChange={(e) => setFilters({ ...filters, minPrice: e[0], maxPrice: e[1] })}
                                                value={[Number(filters?.minPrice) || filterData.price.min, Number(filters?.maxPrice) || filterData.price.max]}
                                                ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
                                                renderThumb={(props, state) => {
                                                    const { key, ...restProps } = props;
                                                    return <div key={key} {...restProps}>{`$${state.valueNow}`}</div>;
                                                }}
                                                pearling
                                                minDistance={10}
                                            />
                                            <p></p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Sort By Filter - Only show if API has sortBy data */}
                            {hasSortBy && availableSortBy.length > 0 && (
                                <div className={styles.filter}>
                                    <button className={showDropDown.sortBy ? `${styles.active}` : ''} onClick={() => showDropDownHandler('sortBy')}>
                                        Sort By :{' '}
                                        {availableSortBy.find((sort) => sort.sortOrder === filters.sortBy && sort.sort === filters.sortOrder)?.name || availableSortBy[0]?.name}
                                    </button>
                                    <div
                                        className={
                                            showDropDown.sortBy
                                                ? `${styles.filterContainer} ${styles.show}`
                                                : `${styles.filterContainer} ${styles.hide}`
                                        }>
                                        <ul className={styles.filtersList}>
                                            {availableSortBy.map(({ name, sort, sortOrder }, index) => (
                                                <li
                                                    key={index}
                                                    className={
                                                        filters.sortBy === sortOrder && filters.sortOrder === sort ? styles.sortBy : ''
                                                    }
                                                    onClick={() => setFilters({ ...filters, sortBy: sortOrder, sortOrder: sort })}>
                                                    {name}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            <div onClick={resetHandler} className={styles.resetFilter}>
                                <img src={iconReset} alt="Reset" />
                                Reset
                            </div>
                        </div>
                    )}
                    <div className={`${styles.unitsWrapper} unitsWrapper`}>
                        {unitsByLocationId?.units &&
                            unitsByLocationId?.units?.map((unit: unitsProps) => (
                                <div key={unit.unitId} className={styles.cardWrapper}>
                                    <div className={styles.card}>
                                        <UnitsCard locationId={locationId} units={unit} showPrice={locationdetail?.isPriceListed} />
                                    </div>
                                </div>
                            ))}
                        {unitsByLocationId.units?.length === 0 && (
                            <div className={styles.noUnits}>
                                <img src={NoUnits} alt="no-units" />
                                <p className={styles.noUnitsFoundText}>No Units Available</p>
                            </div>
                        )}
                    </div>
                    <Faq />
                </div>
            )}
            {/* <OuterFooter /> */}
        </>
    );
};

export default UnitsDashboard;
